import React, { useState, useEffect } from 'react';
import axios from 'axios';

const COMPANY_SIZE_OPTIONS = [
  '1-5 employees',
  '6-50 employees',
  '51-250 employees',
  '250+ employees'
];

function AdminPanel({ user, onLogout, token, branding, refreshBranding }) {
  const [activeTab, setActiveTab] = useState('upload');
  const [documents, setDocuments] = useState([]);
  const [users, setUsers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [accessRequests, setAccessRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Upload form state
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [selectedSectors, setSelectedSectors] = useState([]);
  const [file, setFile] = useState(null);
  const [requiredCompanySize, setRequiredCompanySize] = useState('');

  // Branding form state
  const [brandingCompanyName, setBrandingCompanyName] = useState('');
  const [brandingPrimaryColor, setBrandingPrimaryColor] = useState('');
  const [brandingSecondaryColor, setBrandingSecondaryColor] = useState('');
  const [brandingLogoUrl, setBrandingLogoUrl] = useState('');
  const [brandingAdminEmail, setBrandingAdminEmail] = useState('');

  // Category management state
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryDisplayOrder, setNewCategoryDisplayOrder] = useState('');
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingCategoryOrder, setEditingCategoryOrder] = useState(null);
  const [editingCategoryName, setEditingCategoryName] = useState('');

  // Sector management state
  const [newSectorName, setNewSectorName] = useState('');
  const [newSectorDisplayOrder, setNewSectorDisplayOrder] = useState('');
  const [editingSector, setEditingSector] = useState(null);
  const [editingSectorOrder, setEditingSectorOrder] = useState(null);
  const [editingSectorName, setEditingSectorName] = useState('');

  // Create user form state
  const [newUserCompany, setNewUserCompany] = useState('');
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserPassword, setNewUserPassword] = useState('');
  const [newUserSectors, setNewUserSectors] = useState([]);
  const [newUserCompanySize, setNewUserCompanySize] = useState('');
  const [newUserIsAdmin, setNewUserIsAdmin] = useState(false);

  // Search state
  const [userSearch, setUserSearch] = useState('');
  const [documentSearch, setDocumentSearch] = useState('');

  // Edit document state
  const [editingDocument, setEditingDocument] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editCategory, setEditCategory] = useState('');
  const [editSelectedSectors, setEditSelectedSectors] = useState([]);
  const [editRequiredCompanySize, setEditRequiredCompanySize] = useState('');
  const [editFile, setEditFile] = useState(null);

  // Edit user state
  const [editingUser, setEditingUser] = useState(null);
  const [editUserCompany, setEditUserCompany] = useState('');
  const [editUserEmail, setEditUserEmail] = useState('');
  const [editUserPassword, setEditUserPassword] = useState('');
  const [editUserSectors, setEditUserSectors] = useState([]);
  const [editUserCompanySize, setEditUserCompanySize] = useState('');
  const [editUserIsAdmin, setEditUserIsAdmin] = useState(false);

  // Generated password display
  const [generatedPassword, setGeneratedPassword] = useState(null);
  const [showPasswordModal, setShowPasswordModal] = useState(false);

  useEffect(() => {
    if (activeTab === 'documents') {
      fetchDocuments();
      fetchCategories();
      fetchSectors();
    } else if (activeTab === 'users') {
      fetchUsers();
      fetchSectors();
    } else if (activeTab === 'branding') {
      setBrandingCompanyName(branding.company_name);
      setBrandingPrimaryColor(branding.primary_color);
      setBrandingSecondaryColor(branding.secondary_color);
      setBrandingLogoUrl(branding.logo_url || '');
      setBrandingAdminEmail(branding.admin_email || '');
    } else if (activeTab === 'categories') {
      fetchCategories();
    } else if (activeTab === 'sectors') {
      fetchSectors();
    } else if (activeTab === 'upload') {
      fetchCategories();
      fetchSectors();
    } else if (activeTab === 'access-requests') {
      fetchAccessRequests();
    }
  }, [activeTab, branding]);

  const fetchDocuments = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/api/admin/documents', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setDocuments(response.data);
    } catch (err) {
      setError('Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/api/admin/users', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setUsers(response.data);
    } catch (err) {
      setError('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get('/api/categories');
      setCategories(response.data);
    } catch (err) {
      setError('Failed to load categories');
    }
  };

  const fetchSectors = async () => {
    try {
      const response = await axios.get('/api/sectors');
      setSectors(response.data);
    } catch (err) {
      setError('Failed to load sectors');
    }
  };

  const fetchAccessRequests = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/api/admin/access-requests', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setAccessRequests(response.data);
    } catch (err) {
      setError('Failed to load access requests');
    } finally {
      setLoading(false);
    }
  };

  const handleSectorToggle = (sector) => {
    setSelectedSectors(prev => 
      prev.includes(sector) 
        ? prev.filter(s => s !== sector)
        : [...prev, sector]
    );
  };

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!file) {
      setError('Please select a file');
      return;
    }

    if (selectedSectors.length === 0) {
      setError('Please select at least one sector');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('title', title);
    formData.append('description', description);
    formData.append('category', category);
    formData.append('sectors', JSON.stringify(selectedSectors));
    formData.append('requiredCompanySize', requiredCompanySize);

    setLoading(true);

    try {
      await axios.post('/api/admin/documents', formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      setSuccess('Document uploaded successfully!');
      setTitle('');
      setDescription('');
      setCategory('');
      setSelectedSectors([]);
      setRequiredCompanySize('');
      setFile(null);
      document.getElementById('file-input').value = '';
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to upload document');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteDocument = async (docId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) {
      return;
    }

    try {
      await axios.delete(`/api/admin/documents/${docId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setDocuments(documents.filter(doc => doc.id !== docId));
      setSuccess('Document deleted successfully');
    } catch (err) {
      setError('Failed to delete document');
    }
  };

  const handleBrandingUpdate = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      await axios.put('/api/admin/branding', {
        company_name: brandingCompanyName,
        primary_color: brandingPrimaryColor,
        secondary_color: brandingSecondaryColor,
        logo_url: brandingLogoUrl || null,
        admin_email: brandingAdminEmail || null
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      setSuccess('Branding updated successfully! Refresh the page to see changes.');
      refreshBranding();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update branding');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCategory = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      await axios.post('/api/admin/categories', {
        name: newCategoryName,
        displayOrder: parseInt(newCategoryDisplayOrder) || 0
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      setSuccess('Category created successfully!');
      setNewCategoryName('');
      setNewCategoryDisplayOrder('');
      fetchCategories();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create category');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCategory = async (id) => {
    if (!window.confirm('Are you sure you want to delete this category? Documents in this category will become uncategorised.')) {
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await axios.delete(`/api/admin/categories/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      setSuccess('Category deleted successfully');
      fetchCategories();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete category');
    } finally {
      setLoading(false);
    }
  };

  const handleEditCategoryOrder = (id, currentOrder, currentName) => {
    setEditingCategory(id);
    setEditingCategoryOrder(currentOrder);
    setEditingCategoryName(currentName);
  };

  const handleSaveEditedCategory = async (id) => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await axios.put(`/api/admin/categories/${id}`, {
        name: editingCategoryName,
        displayOrder: parseInt(editingCategoryOrder)
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      setSuccess('Category updated successfully');
      setEditingCategory(null);
      setEditingCategoryOrder(null);
      setEditingCategoryName('');
      fetchCategories();
    } catch (err) {
      setError(err.response?.data?.error || 'Error updating category');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSector = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      await axios.post('/api/admin/sectors', {
        name: newSectorName,
        displayOrder: parseInt(newSectorDisplayOrder) || 0
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      setSuccess('Sector created successfully!');
      setNewSectorName('');
      setNewSectorDisplayOrder('');
      fetchSectors();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create sector');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSector = async (id) => {
    if (!window.confirm('Are you sure you want to delete this sector? This will affect documents and users assigned to this sector.')) {
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await axios.delete(`/api/admin/sectors/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      setSuccess('Sector deleted successfully');
      fetchSectors();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete sector');
    } finally {
      setLoading(false);
    }
  };

  const handleEditSectorOrder = (id, currentOrder, currentName) => {
    setEditingSector(id);
    setEditingSectorOrder(currentOrder);
    setEditingSectorName(currentName);
  };

  const handleSaveEditedSectorOrder = async (id) => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await axios.put(`/api/admin/sectors/${id}`, {
        name: editingSectorName,
        displayOrder: parseInt(editingSectorOrder)
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      setSuccess('Sector updated successfully');
      setEditingSector(null);
      setEditingSectorOrder(null);
      setEditingSectorName('');
      fetchSectors();
    } catch (err) {
      setError(err.response?.data?.error || 'Error updating sector');
    } finally {
      setLoading(false);
    }
  };

  const generatePassword = () => {
    const length = 12;
    const charset = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*";
    let password = "";
    for (let i = 0; i < length; i++) {
      password += charset.charAt(Math.floor(Math.random() * charset.length));
    }
    return password;
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (newUserSectors.length === 0 && !newUserIsAdmin) {
      setError('Please select at least one sector (not required for admin users)');
      return;
    }

    setLoading(true);

    const passwordToUse = newUserPassword || generatePassword();

    try {
      await axios.post('/api/admin/users', {
        companyName: newUserCompany,
        email: newUserEmail,
        password: passwordToUse,
        sectors: newUserSectors,
        companySize: newUserCompanySize,
        isAdmin: newUserIsAdmin
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!newUserPassword) {
        setGeneratedPassword({
          email: newUserEmail,
          password: passwordToUse,
          companyName: newUserCompany
        });
        setShowPasswordModal(true);
      } else {
        setSuccess('User created successfully!');
      }

      setNewUserCompany('');
      setNewUserEmail('');
      setNewUserPassword('');
      setNewUserSectors([]);
      setNewUserCompanySize('');
      setNewUserIsAdmin(false);
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create user');
    } finally {
      setLoading(false);
    }
  };

  const handleUserSectorToggle = (sector) => {
    setNewUserSectors(prev => 
      prev.includes(sector) 
        ? prev.filter(s => s !== sector)
        : [...prev, sector]
    );
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await axios.delete(`/api/admin/users/${userId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      setSuccess('User deleted successfully');
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete user');
    } finally {
      setLoading(false);
    }
  };

  const handleEditUser = (user) => {
    setEditingUser(user.id);
    setEditUserCompany(user.company_name);
    setEditUserEmail(user.email);
    setEditUserPassword('');
    setEditUserSectors(user.sectors || []);
    setEditUserCompanySize(user.company_size || '');
    setEditUserIsAdmin(user.is_admin || false);
  };

  const handleCancelEdit = () => {
    setEditingUser(null);
    setEditUserCompany('');
    setEditUserEmail('');
    setEditUserPassword('');
    setEditUserSectors([]);
    setEditUserCompanySize('');
    setEditUserIsAdmin(false);
  };

  const handleEditUserSectorToggle = (sector) => {
    setEditUserSectors(prev => 
      prev.includes(sector) 
        ? prev.filter(s => s !== sector)
        : [...prev, sector]
    );
  };

  const handleUpdateUser = async (userId) => {
    setError('');
    setSuccess('');

    if (editUserSectors.length === 0 && !editUserIsAdmin) {
      setError('Please select at least one sector (not required for admin users)');
      return;
    }

    setLoading(true);

    try {
      const updateData = {
        companyName: editUserCompany,
        email: editUserEmail,
        sectors: editUserSectors,
        companySize: editUserCompanySize,
        isAdmin: editUserIsAdmin
      };

      if (editUserPassword) {
        updateData.password = editUserPassword;
      }

      await axios.put(`/api/admin/users/${userId}`, updateData, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      setSuccess('User updated successfully!');
      setEditingUser(null);
      setEditUserCompany('');
      setEditUserEmail('');
      setEditUserPassword('');
      setEditUserSectors([]);
      setEditUserCompanySize('');
      setEditUserIsAdmin(false);
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update user');
    } finally {
      setLoading(false);
    }
  };

  const handleEditDocument = (doc) => {
    setEditingDocument(doc.id);
    setEditTitle(doc.title);
    setEditDescription(doc.description || '');
    setEditCategory(doc.category || '');
    setEditSelectedSectors(doc.sectors || []);
    setEditRequiredCompanySize(doc.requiredCompanySize || '');
  };

  const handleCancelEditDocument = () => {
    setEditingDocument(null);
    setEditTitle('');
    setEditDescription('');
    setEditCategory('');
    setEditSelectedSectors([]);
    setEditRequiredCompanySize('');
    setEditFile(null);
  };

  const handleEditSectorToggle = (sector) => {
    setEditSelectedSectors(prev => 
      prev.includes(sector) 
        ? prev.filter(s => s !== sector)
        : [...prev, sector]
    );
  };

  const handleUpdateDocument = async (docId) => {
    setError('');
    setSuccess('');

    if (editSelectedSectors.length === 0) {
      setError('Please select at least one sector');
      return;
    }

    setLoading(true);

    try {
      const formData = new FormData();
      formData.append('title', editTitle);
      formData.append('description', editDescription);
      formData.append('category', editCategory);
      formData.append('sectors', JSON.stringify(editSelectedSectors));
      formData.append('requiredCompanySize', editRequiredCompanySize);
      
      // Add file if one was selected
      if (editFile) {
        formData.append('file', editFile);
      }

      await axios.put(`/api/admin/documents/${docId}`, formData, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      const successMsg = editFile ? 'Document and file updated successfully!' : 'Document updated successfully!';
      setSuccess(successMsg);
      setEditingDocument(null);
      setEditTitle('');
      setEditDescription('');
      setEditCategory('');
      setEditSelectedSectors([]);
      setEditRequiredCompanySize('');
      setEditFile(null);
      fetchDocuments();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update document');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAccessRequest = async (requestId) => {
    if (!window.confirm('Are you sure you want to delete this access request?')) {
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await axios.delete(`/api/admin/access-requests/${requestId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      setSuccess('Access request deleted successfully');
      fetchAccessRequests();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete access request');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getFilteredUsers = () => {
    if (!userSearch.trim()) {
      return users;
    }

    const search = userSearch.toLowerCase();
    return users.filter(user => {
      const companyMatch = user.company_name.toLowerCase().includes(search);
      const emailMatch = user.email.toLowerCase().includes(search);
      const sectorsMatch = user.sectors.some(sector => sector.toLowerCase().includes(search));
      return companyMatch || emailMatch || sectorsMatch;
    });
  };

  const getFilteredDocuments = () => {
    if (!documentSearch.trim()) {
      return documents;
    }

    const search = documentSearch.toLowerCase().trim();
    
    return documents.filter(doc => {
      // Check title
      if (doc.title && doc.title.toLowerCase().includes(search)) {
        return true;
      }
      
      // Check description
      if (doc.description && doc.description.toLowerCase().includes(search)) {
        return true;
      }
      
      // Check category
      if (doc.category && doc.category.toLowerCase().includes(search)) {
        return true;
      }
      
      // Check sectors array
      if (doc.sectors && Array.isArray(doc.sectors)) {
        for (let sector of doc.sectors) {
          if (sector && typeof sector === 'string' && sector.toLowerCase().includes(search)) {
            return true;
          }
        }
      }
      
      return false;
    });
  };

  return (
    <div className="app-container">
      <header className="header">
        <div className="header-content">
          <div className="logo">
            {branding.logo_url && (
              <img 
                src={branding.logo_url} 
                alt={branding.company_name}
                style={{
                  maxHeight: '50px',
                  height: 'auto',
                  maxWidth: '250px',
                  display: 'block'
                }}
              />
            )}
            {!branding.logo_url && `${branding.company_name} - Admin`}
          </div>
          <div className="header-right">
            <div className="user-info">
              <div className="user-email">{user.email}</div>
            </div>
            <button onClick={onLogout} className="btn-logout">Logout</button>
          </div>
        </div>
      </header>

      <main className="main-content">
        <div className="admin-tabs">
          <button 
            onClick={() => setActiveTab('upload')} 
            className={`tab-button ${activeTab === 'upload' ? 'active' : ''}`}
          >
            Upload Documents
          </button>
          <button 
            onClick={() => setActiveTab('documents')} 
            className={`tab-button ${activeTab === 'documents' ? 'active' : ''}`}
          >
            Manage Documents
          </button>
          <button 
            onClick={() => setActiveTab('users')} 
            className={`tab-button ${activeTab === 'users' ? 'active' : ''}`}
          >
            Manage Users
          </button>
          <button 
            onClick={() => setActiveTab('access-requests')} 
            className={`tab-button ${activeTab === 'access-requests' ? 'active' : ''}`}
          >
            Access Requests
          </button>
          <button 
            onClick={() => setActiveTab('categories')} 
            className={`tab-button ${activeTab === 'categories' ? 'active' : ''}`}
          >
            Categories
          </button>
          <button 
            onClick={() => setActiveTab('sectors')} 
            className={`tab-button ${activeTab === 'sectors' ? 'active' : ''}`}
          >
            Sectors
          </button>
          <button 
            onClick={() => setActiveTab('branding')} 
            className={`tab-button ${activeTab === 'branding' ? 'active' : ''}`}
          >
            Branding
          </button>
        </div>

        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}

        {activeTab === 'upload' && (
          <div className="admin-section">
            <h2 className="section-title">Upload New Document</h2>
            <form onSubmit={handleUpload} className="upload-form">
              <div className="form-group">
                <label htmlFor="title" className="form-label">Document Title *</label>
                <input
                  type="text"
                  id="title"
                  className="form-input"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  required
                  placeholder="e.g., Fire Safety Policy"
                />
              </div>

              <div className="form-group">
                <label htmlFor="description" className="form-label">Description</label>
                <textarea
                  id="description"
                  className="form-input"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows="3"
                  placeholder="Brief description of the document..."
                />
              </div>

              <div className="form-group">
                <label htmlFor="category" className="form-label">Category</label>
                <select
                  id="category"
                  className="form-input"
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                >
                  <option value="">Select a category (optional)</option>
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.name}>{cat.name}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Applicable Sectors *</label>
                <div className="sector-checkboxes">
                  {sectors.map(sector => (
                    <div key={sector.id} className="sector-checkbox">
                      <input
                        type="checkbox"
                        id={`upload-sector-${sector.id}`}
                        checked={selectedSectors.includes(sector.name)}
                        onChange={() => handleSectorToggle(sector.name)}
                      />
                      <label htmlFor={`upload-sector-${sector.id}`}>{sector.name}</label>
                    </div>
                  ))}
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Required Company Size</label>
                <select
                  className="form-input"
                  value={requiredCompanySize}
                  onChange={(e) => setRequiredCompanySize(e.target.value)}
                >
                  <option value="">All companies (no restriction)</option>
                  {COMPANY_SIZE_OPTIONS.map(size => (
                    <option key={size} value={size}>{size}+</option>
                  ))}
                </select>
                <small style={{ color: '#666', fontSize: '0.85rem', display: 'block', marginTop: '0.5rem' }}>
                  Only companies of this size or larger will see this document
                </small>
              </div>

              <div className="form-group">
                <label htmlFor="file-input" className="form-label">Select File *</label>
                <input
                  type="file"
                  id="file-input"
                  className="form-input"
                  onChange={handleFileChange}
                  required
                  accept=".pdf,.doc,.docx,.xls,.xlsx"
                />
              </div>

              <button type="submit" className="btn-primary" disabled={loading}>
                {loading ? 'Uploading...' : 'Upload Document'}
              </button>
            </form>
          </div>
        )}

        {activeTab === 'documents' && (
          <div className="admin-section">
            <div className="section-header">
              <h2 className="section-title">Manage Documents</h2>
              <span className="document-count">{documents.length} total documents</span>
            </div>

            <div style={{ marginBottom: '2rem' }}>
              <input
                type="text"
                className="form-input"
                value={documentSearch}
                onChange={(e) => setDocumentSearch(e.target.value)}
                placeholder="Search documents by title, description, or category..."
                style={{ maxWidth: '600px' }}
              />
              {documentSearch && (
                <div style={{ marginTop: '0.5rem', fontSize: '0.9rem', color: '#666' }}>
                  Showing {getFilteredDocuments().length} of {documents.length} documents
                </div>
              )}
            </div>

            {loading ? (
              <div className="loading">Loading documents...</div>
            ) : getFilteredDocuments().length === 0 ? (
              <div className="no-documents">
                {documentSearch ? `No documents found matching "${documentSearch}"` : 'No documents uploaded yet'}
              </div>
            ) : (
              <div className="documents-table-container">
                <table className="documents-table">
                  <thead>
                    <tr>
                      <th>Title</th>
                      <th>Category</th>
                      <th>Sectors</th>
                      <th>Required Size</th>
                      <th>Last Updated</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {getFilteredDocuments().map(doc => (
                      <React.Fragment key={doc.id}>
                        {editingDocument === doc.id ? (
                          <tr className="edit-row">
                            <td colSpan="6">
                              <div style={{ padding: '1rem' }}>
                                <h4 style={{ marginBottom: '1rem' }}>Edit Document</h4>
                                <div className="form-group">
                                  <label className="form-label">Title *</label>
                                  <input
                                    type="text"
                                    className="form-input"
                                    value={editTitle}
                                    onChange={(e) => setEditTitle(e.target.value)}
                                    required
                                  />
                                </div>
                                <div className="form-group">
                                  <label className="form-label">Description</label>
                                  <textarea
                                    className="form-input"
                                    value={editDescription}
                                    onChange={(e) => setEditDescription(e.target.value)}
                                    rows="3"
                                  />
                                </div>
                                <div className="form-group">
                                  <label className="form-label">Category</label>
                                  <select
                                    className="form-input"
                                    value={editCategory}
                                    onChange={(e) => setEditCategory(e.target.value)}
                                  >
                                    <option value="">Select a category (optional)</option>
                                    {categories.map(cat => (
                                      <option key={cat.id} value={cat.name}>{cat.name}</option>
                                    ))}
                                  </select>
                                </div>
                                <div className="form-group">
                                  <label className="form-label">Applicable Sectors *</label>
                                  <div className="sector-checkboxes">
                                    {sectors.map(sector => (
                                      <div key={sector.id} className="sector-checkbox">
                                        <input
                                          type="checkbox"
                                          id={`edit-sector-${sector.id}`}
                                          checked={editSelectedSectors.includes(sector.name)}
                                          onChange={() => handleEditSectorToggle(sector.name)}
                                        />
                                        <label htmlFor={`edit-sector-${sector.id}`}>{sector.name}</label>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                                <div className="form-group">
                                  <label className="form-label">Required Company Size</label>
                                  <select
                                    className="form-input"
                                    value={editRequiredCompanySize}
                                    onChange={(e) => setEditRequiredCompanySize(e.target.value)}
                                  >
                                    <option value="">All companies (no restriction)</option>
                                    {COMPANY_SIZE_OPTIONS.map(size => (
                                      <option key={size} value={size}>{size}+</option>
                                    ))}
                                  </select>
                                  <small style={{ color: '#666', fontSize: '0.85rem', display: 'block', marginTop: '0.5rem' }}>
                                    Only companies of this size or larger will see this document
                                  </small>
                                </div>
                                <div className="form-group">
                                  <label className="form-label">Replace Document File (Optional)</label>
                                  <input
                                    type="file"
                                    className="form-input"
                                    onChange={(e) => setEditFile(e.target.files[0])}
                                    accept=".pdf,.doc,.docx,.xls,.xlsx"
                                  />
                                  <small style={{ color: '#666', fontSize: '0.85rem', display: 'block', marginTop: '0.5rem' }}>
                                    {editFile ? (
                                      <span style={{ color: '#28a745', fontWeight: '500' }}>
                                        New file selected: {editFile.name}
                                      </span>
                                    ) : (
                                      'Leave blank to keep the existing file. Select a new file to replace it.'
                                    )}
                                  </small>
                                </div>
                                <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
                                  <button
                                    onClick={() => handleUpdateDocument(doc.id)}
                                    className="btn-primary"
                                    disabled={loading}
                                  >
                                    Save Changes
                                  </button>
                                  <button
                                    onClick={handleCancelEditDocument}
                                    className="btn-secondary"
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </div>
                            </td>
                          </tr>
                        ) : (
                          <tr>
                            <td>{doc.title}</td>
                            <td>{doc.category || 'Uncategorised'}</td>
                            <td>
                              <div className="document-sectors">
                                {doc.sectors.map(sector => (
                                  <span key={sector} className="document-sector">{sector}</span>
                                ))}
                              </div>
                            </td>
                            <td>
                              {doc.requiredCompanySize ? (
                                <span style={{ 
                                  fontSize: '0.85rem', 
                                  padding: '0.25rem 0.5rem', 
                                  backgroundColor: '#f0f0f0', 
                                  borderRadius: '3px' 
                                }}>
                                  {doc.requiredCompanySize}+
                                </span>
                              ) : (
                                <span style={{ color: '#999', fontSize: '0.85rem' }}>All</span>
                              )}
                            </td>
                            <td>{formatDate(doc.updatedAt)}</td>
                            <td>
                              <div className="action-buttons">
                                <button 
                                  onClick={() => handleEditDocument(doc)}
                                  className="btn-edit"
                                >
                                  Edit
                                </button>
                                <button 
                                  onClick={() => handleDeleteDocument(doc.id)}
                                  className="btn-delete"
                                >
                                  Delete
                                </button>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {activeTab === 'users' && (
          <div className="admin-section">
            <h2 className="section-title">Manage Users</h2>

            <div className="user-management-container">
              <div className="create-user-section">
                <h3 style={{ marginBottom: '1rem' }}>Create New User</h3>
                <form onSubmit={handleCreateUser}>
                  <div className="form-group">
                    <label className="form-label">Company Name *</label>
                    <input
                      type="text"
                      className="form-input"
                      value={newUserCompany}
                      onChange={(e) => setNewUserCompany(e.target.value)}
                      required
                      placeholder="Company Name"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Email *</label>
                    <input
                      type="email"
                      className="form-input"
                      value={newUserEmail}
                      onChange={(e) => setNewUserEmail(e.target.value)}
                      required
                      placeholder="user@company.com"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Password (leave blank to auto-generate)</label>
                    <input
                      type="text"
                      className="form-input"
                      value={newUserPassword}
                      onChange={(e) => setNewUserPassword(e.target.value)}
                      placeholder="Auto-generate if blank"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Company Size</label>
                    <select
                      className="form-input"
                      value={newUserCompanySize}
                      onChange={(e) => setNewUserCompanySize(e.target.value)}
                    >
                      <option value="">Select company size (optional)</option>
                      {COMPANY_SIZE_OPTIONS.map(size => (
                        <option key={size} value={size}>{size}</option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label className="form-label">
                      Sectors {!newUserIsAdmin && '*'}
                      {newUserIsAdmin && <span style={{ color: '#666', fontWeight: 'normal' }}> (optional for admin users)</span>}
                    </label>
                    <div className="sector-checkboxes">
                      {sectors.map(sector => (
                        <div key={sector.id} className="sector-checkbox">
                          <input
                            type="checkbox"
                            id={`user-sector-${sector.id}`}
                            checked={newUserSectors.includes(sector.name)}
                            onChange={() => handleUserSectorToggle(sector.name)}
                          />
                          <label htmlFor={`user-sector-${sector.id}`}>{sector.name}</label>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="form-group">
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '0.5rem',
                      padding: '0.75rem',
                      backgroundColor: '#fff3cd',
                      borderRadius: '4px',
                      border: '1px solid #ffc107'
                    }}>
                      <input
                        type="checkbox"
                        id="newUserIsAdmin"
                        checked={newUserIsAdmin}
                        onChange={(e) => setNewUserIsAdmin(e.target.checked)}
                        style={{ cursor: 'pointer' }}
                      />
                      <label htmlFor="newUserIsAdmin" style={{ 
                        cursor: 'pointer', 
                        margin: 0,
                        fontWeight: '500'
                      }}>
                        Admin User (grants full admin panel access)
                      </label>
                    </div>
                  </div>

                  <button type="submit" className="btn-primary" disabled={loading}>
                    {loading ? 'Creating...' : 'Create User'}
                  </button>
                </form>
              </div>

              <div className="users-list-section">
                <h3 style={{ marginBottom: '1rem' }}>Existing Users ({users.length})</h3>
                
                <div style={{ marginBottom: '1rem' }}>
                  <input
                    type="text"
                    className="form-input"
                    value={userSearch}
                    onChange={(e) => setUserSearch(e.target.value)}
                    placeholder="Search users by company, email, or sector..."
                  />
                  {userSearch && (
                    <div style={{ marginTop: '0.5rem', fontSize: '0.9rem', color: '#666' }}>
                      Showing {getFilteredUsers().length} of {users.length} users
                    </div>
                  )}
                </div>

                {loading ? (
                  <div className="loading">Loading users...</div>
                ) : getFilteredUsers().length === 0 ? (
                  <div className="no-documents">
                    {userSearch ? `No users found matching "${userSearch}"` : 'No users created yet'}
                  </div>
                ) : (
                  <div className="users-table-container">
                    <table className="documents-table">
                      <thead>
                        <tr>
                          <th>Company</th>
                          <th>Email</th>
                          <th>Company Size</th>
                          <th>Admin</th>
                          <th>Sectors</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {getFilteredUsers().map(usr => (
                          <React.Fragment key={usr.id}>
                            {editingUser === usr.id ? (
                              <tr className="edit-row">
                                <td colSpan="6">
                                  <div style={{ padding: '1rem' }}>
                                    <h4 style={{ marginBottom: '1rem' }}>Edit User</h4>
                                    <div className="form-group">
                                      <label className="form-label">Company Name *</label>
                                      <input
                                        type="text"
                                        className="form-input"
                                        value={editUserCompany}
                                        onChange={(e) => setEditUserCompany(e.target.value)}
                                        required
                                      />
                                    </div>
                                    <div className="form-group">
                                      <label className="form-label">Email *</label>
                                      <input
                                        type="email"
                                        className="form-input"
                                        value={editUserEmail}
                                        onChange={(e) => setEditUserEmail(e.target.value)}
                                        required
                                      />
                                    </div>
                                    <div className="form-group">
                                      <label className="form-label">New Password (leave blank to keep current)</label>
                                      <input
                                        type="text"
                                        className="form-input"
                                        value={editUserPassword}
                                        onChange={(e) => setEditUserPassword(e.target.value)}
                                        placeholder="Leave blank to keep current password"
                                      />
                                    </div>
                                    <div className="form-group">
                                      <label className="form-label">Company Size</label>
                                      <select
                                        className="form-input"
                                        value={editUserCompanySize}
                                        onChange={(e) => setEditUserCompanySize(e.target.value)}
                                      >
                                        <option value="">Select company size (optional)</option>
                                        {COMPANY_SIZE_OPTIONS.map(size => (
                                          <option key={size} value={size}>{size}</option>
                                        ))}
                                      </select>
                                    </div>
                                    <div className="form-group">
                                      <label className="form-label">
                                        Sectors {!editUserIsAdmin && '*'}
                                        {editUserIsAdmin && <span style={{ color: '#666', fontWeight: 'normal' }}> (optional for admin users)</span>}
                                      </label>
                                      <div className="sector-checkboxes">
                                        {sectors.map(sector => (
                                          <div key={sector.id} className="sector-checkbox">
                                            <input
                                              type="checkbox"
                                              id={`edit-user-sector-${sector.id}`}
                                              checked={editUserSectors.includes(sector.name)}
                                              onChange={() => handleEditUserSectorToggle(sector.name)}
                                            />
                                            <label htmlFor={`edit-user-sector-${sector.id}`}>{sector.name}</label>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                    <div className="form-group">
                                      <div style={{ 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        gap: '0.5rem',
                                        padding: '0.75rem',
                                        backgroundColor: '#fff3cd',
                                        borderRadius: '4px',
                                        border: '1px solid #ffc107'
                                      }}>
                                        <input
                                          type="checkbox"
                                          id="editUserIsAdmin"
                                          checked={editUserIsAdmin}
                                          onChange={(e) => setEditUserIsAdmin(e.target.checked)}
                                          style={{ cursor: 'pointer' }}
                                        />
                                        <label htmlFor="editUserIsAdmin" style={{ 
                                          cursor: 'pointer', 
                                          margin: 0,
                                          fontWeight: '500'
                                        }}>
                                          Admin User (grants full admin panel access)
                                        </label>
                                      </div>
                                    </div>
                                    <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
                                      <button
                                        onClick={() => handleUpdateUser(usr.id)}
                                        className="btn-primary"
                                        disabled={loading}
                                      >
                                        Save Changes
                                      </button>
                                      <button
                                        onClick={handleCancelEdit}
                                        className="btn-secondary"
                                      >
                                        Cancel
                                      </button>
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            ) : (
                              <tr>
                                <td>{usr.company_name}</td>
                                <td>{usr.email}</td>
                                <td>{usr.company_size || 'Not specified'}</td>
                                <td>
                                  {usr.is_admin ? (
                                    <span style={{ 
                                      color: '#fff',
                                      backgroundColor: '#dc3545',
                                      padding: '0.25rem 0.5rem',
                                      borderRadius: '4px',
                                      fontSize: '0.85rem',
                                      fontWeight: '500'
                                    }}>
                                      Admin
                                    </span>
                                  ) : (
                                    <span style={{ color: '#666' }}>User</span>
                                  )}
                                </td>
                                <td>
                                  <div className="document-sectors">
                                    {usr.sectors.map(sector => (
                                      <span key={sector} className="document-sector">{sector}</span>
                                    ))}
                                  </div>
                                </td>
                                <td>
                                  <div className="action-buttons">
                                    <button 
                                      onClick={() => handleEditUser(usr)}
                                      className="btn-edit"
                                    >
                                      Edit
                                    </button>
                                    <button 
                                      onClick={() => handleDeleteUser(usr.id)}
                                      className="btn-delete"
                                    >
                                      Delete
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'access-requests' && (
          <div className="admin-section">
            <div className="section-header">
              <h2 className="section-title">Access Requests</h2>
              <span className="document-count">{accessRequests.length} pending requests</span>
            </div>

            {loading ? (
              <div className="loading">Loading access requests...</div>
            ) : accessRequests.length === 0 ? (
              <div className="no-documents">No access requests at this time</div>
            ) : (
              <div className="access-requests-container">
                {accessRequests.map(request => (
                  <div key={request.id} className="access-request-card">
                    <div className="request-header">
                      <h3>{request.company_name}</h3>
                      <span className="request-date">{formatDate(request.created_at)}</span>
                    </div>
                    <div className="request-details">
                      <div><strong>Contact:</strong> {request.contact_name}</div>
                      <div><strong>Email:</strong> {request.email}</div>
                      {request.phone && <div><strong>Phone:</strong> {request.phone}</div>}
                      {request.company_size && <div><strong>Company Size:</strong> {request.company_size}</div>}
                      <div>
                        <strong>Requested Sectors:</strong>
                        <div className="document-sectors">
                          {request.sectors.map(sector => (
                            <span key={sector} className="document-sector">{sector}</span>
                          ))}
                        </div>
                      </div>
                      {request.message && (
                        <div>
                          <strong>Message:</strong>
                          <p style={{ marginTop: '0.5rem', padding: '0.5rem', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
                            {request.message}
                          </p>
                        </div>
                      )}
                    </div>
                    <div className="request-actions">
                      <button
                        onClick={() => handleDeleteAccessRequest(request.id)}
                        className="btn-delete"
                      >
                        Delete Request
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'categories' && (
          <div className="admin-section">
            <h2 className="section-title">Manage Categories</h2>
            
            <div className="category-management-container">
              <div className="create-category-section">
                <h3 style={{ marginBottom: '1rem' }}>Create New Category</h3>
                <form onSubmit={handleCreateCategory}>
                  <div className="form-group">
                    <label className="form-label">Category Name *</label>
                    <input
                      type="text"
                      className="form-input"
                      value={newCategoryName}
                      onChange={(e) => setNewCategoryName(e.target.value)}
                      required
                      placeholder="e.g., Health & Safety Policies"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Display Order</label>
                    <input
                      type="number"
                      className="form-input"
                      value={newCategoryDisplayOrder}
                      onChange={(e) => setNewCategoryDisplayOrder(e.target.value)}
                      placeholder="0"
                      min="0"
                    />
                    <small style={{ color: '#666', fontSize: '0.85rem' }}>
                      Lower numbers appear first (0 = first)
                    </small>
                  </div>

                  <button type="submit" className="btn-primary" disabled={loading}>
                    {loading ? 'Creating...' : 'Create Category'}
                  </button>
                </form>
              </div>

              <div className="categories-list-section">
                <h3 style={{ marginBottom: '1rem' }}>Existing Categories ({categories.length})</h3>
                {categories.length === 0 ? (
                  <div className="no-documents">No categories created yet</div>
                ) : (
                  <div className="categories-table-container">
                    <table className="documents-table">
                      <thead>
                        <tr>
                          <th>Category Name</th>
                          <th>Display Order</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {categories.map(cat => (
                          <tr key={cat.id}>
                            <td>
                              {editingCategory === cat.id ? (
                                <input
                                  type="text"
                                  className="form-input"
                                  value={editingCategoryName}
                                  onChange={(e) => setEditingCategoryName(e.target.value)}
                                  style={{ width: '100%' }}
                                />
                              ) : (
                                cat.name
                              )}
                            </td>
                            <td>
                              {editingCategory === cat.id ? (
                                <input
                                  type="number"
                                  className="form-input"
                                  value={editingCategoryOrder}
                                  onChange={(e) => setEditingCategoryOrder(e.target.value)}
                                  style={{ width: '100px' }}
                                  min="0"
                                />
                              ) : (
                                cat.display_order
                              )}
                            </td>
                            <td>
                              {editingCategory === cat.id ? (
                                <div style={{ display: 'flex', gap: '0.5rem' }}>
                                  <button
                                    onClick={() => handleSaveEditedCategory(cat.id)}
                                    className="btn-primary"
                                    disabled={loading}
                                    style={{ fontSize: '0.85rem', padding: '0.4rem 0.8rem' }}
                                  >
                                    Save
                                  </button>
                                  <button
                                    onClick={() => {
                                      setEditingCategory(null);
                                      setEditingCategoryOrder(null);
                                      setEditingCategoryName('');
                                    }}
                                    className="btn-secondary"
                                    style={{ fontSize: '0.85rem', padding: '0.4rem 0.8rem' }}
                                  >
                                    Cancel
                                  </button>
                                </div>
                              ) : (
                                <div style={{ display: 'flex', gap: '0.5rem' }}>
                                  <button
                                    onClick={() => handleEditCategoryOrder(cat.id, cat.display_order, cat.name)}
                                    className="btn-primary"
                                    style={{ fontSize: '0.85rem', padding: '0.4rem 0.8rem' }}
                                  >
                                    Edit
                                  </button>
                                  <button
                                    onClick={() => handleDeleteCategory(cat.id)}
                                    className="btn-delete"
                                    style={{ fontSize: '0.85rem', padding: '0.4rem 0.8rem' }}
                                  >
                                    Delete
                                  </button>
                                </div>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'sectors' && (
          <div className="admin-section">
            <h2 className="section-title">Manage Sectors</h2>
            
            <div className="category-management-container">
              <div className="create-category-section">
                <h3 style={{ marginBottom: '1rem' }}>Create New Sector</h3>
                <form onSubmit={handleCreateSector}>
                  <div className="form-group">
                    <label className="form-label">Sector Name *</label>
                    <input
                      type="text"
                      className="form-input"
                      value={newSectorName}
                      onChange={(e) => setNewSectorName(e.target.value)}
                      required
                      placeholder="e.g., Healthcare"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Display Order</label>
                    <input
                      type="number"
                      className="form-input"
                      value={newSectorDisplayOrder}
                      onChange={(e) => setNewSectorDisplayOrder(e.target.value)}
                      placeholder="0"
                      min="0"
                    />
                    <small style={{ color: '#666', fontSize: '0.85rem' }}>
                      Lower numbers appear first (0 = first)
                    </small>
                  </div>

                  <button type="submit" className="btn-primary" disabled={loading}>
                    {loading ? 'Creating...' : 'Create Sector'}
                  </button>
                </form>
              </div>

              <div className="categories-list-section">
                <h3 style={{ marginBottom: '1rem' }}>Existing Sectors ({sectors.length})</h3>
                {sectors.length === 0 ? (
                  <div className="no-documents">No sectors created yet</div>
                ) : (
                  <div className="categories-table-container">
                    <table className="documents-table">
                      <thead>
                        <tr>
                          <th>Sector Name</th>
                          <th>Display Order</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sectors.map(sect => (
                          <tr key={sect.id}>
                            <td>
                              {editingSector === sect.id ? (
                                <input
                                  type="text"
                                  className="form-input"
                                  value={editingSectorName}
                                  onChange={(e) => setEditingSectorName(e.target.value)}
                                  placeholder="Sector name"
                                />
                              ) : (
                                sect.name
                              )}
                            </td>
                            <td>
                              {editingSector === sect.id ? (
                                <input
                                  type="number"
                                  className="form-input"
                                  value={editingSectorOrder !== null ? editingSectorOrder : ''}
                                  onChange={(e) => setEditingSectorOrder(e.target.value)}
                                  style={{ width: '100px' }}
                                  min="0"
                                />
                              ) : (
                                sect.display_order
                              )}
                            </td>
                            <td>
                              {editingSector === sect.id ? (
                                <div style={{ display: 'flex', gap: '0.5rem' }}>
                                  <button
                                    onClick={() => handleSaveEditedSectorOrder(sect.id)}
                                    className="btn-primary"
                                    disabled={loading}
                                  >
                                    Save
                                  </button>
                                  <button
                                    onClick={() => {
                                      setEditingSector(null);
                                      setEditingSectorOrder(null);
                                      setEditingSectorName('');
                                    }}
                                    className="btn-secondary"
                                  >
                                    Cancel
                                  </button>
                                </div>
                              ) : (
                                <div style={{ display: 'flex', gap: '0.5rem' }}>
                                  <button
                                    onClick={() => handleEditSectorOrder(sect.id, sect.display_order, sect.name)}
                                    className="btn-edit"
                                  >
                                    Edit
                                  </button>
                                  <button
                                    onClick={() => handleDeleteSector(sect.id)}
                                    className="btn-delete"
                                  >
                                    Delete
                                  </button>
                                </div>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'branding' && (
          <div className="admin-section">
            <h2 className="section-title">Platform Branding</h2>
            <form onSubmit={handleBrandingUpdate} className="branding-form">
              <div className="form-group">
                <label htmlFor="brandingCompanyName" className="form-label">Company Name *</label>
                <input
                  type="text"
                  id="brandingCompanyName"
                  className="form-input"
                  value={brandingCompanyName}
                  onChange={(e) => setBrandingCompanyName(e.target.value)}
                  required
                  placeholder="e.g., Your Company Name"
                />
                <small style={{ color: '#666', fontSize: '0.85rem' }}>
                  This appears in the header and login pages
                </small>
              </div>

              <div className="form-group">
                <label htmlFor="brandingPrimaryColor" className="form-label">Primary Colour *</label>
                <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                  <input
                    type="color"
                    id="brandingPrimaryColor"
                    value={brandingPrimaryColor}
                    onChange={(e) => setBrandingPrimaryColor(e.target.value)}
                    required
                    style={{ width: '60px', height: '40px', cursor: 'pointer', border: '1px solid #ddd', borderRadius: '4px' }}
                  />
                  <input
                    type="text"
                    className="form-input"
                    value={brandingPrimaryColor}
                    onChange={(e) => setBrandingPrimaryColor(e.target.value)}
                    placeholder="#4a90e2"
                    pattern="^#[0-9A-Fa-f]{6}$"
                    style={{ flex: 1 }}
                  />
                </div>
                <small style={{ color: '#666', fontSize: '0.85rem' }}>
                  Used for buttons, links, and accents
                </small>
              </div>

              <div className="form-group">
                <label htmlFor="brandingSecondaryColor" className="form-label">Secondary Colour *</label>
                <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                  <input
                    type="color"
                    id="brandingSecondaryColor"
                    value={brandingSecondaryColor}
                    onChange={(e) => setBrandingSecondaryColor(e.target.value)}
                    required
                    style={{ width: '60px', height: '40px', cursor: 'pointer', border: '1px solid #ddd', borderRadius: '4px' }}
                  />
                  <input
                    type="text"
                    className="form-input"
                    value={brandingSecondaryColor}
                    onChange={(e) => setBrandingSecondaryColor(e.target.value)}
                    placeholder="#1a1a1a"
                    pattern="^#[0-9A-Fa-f]{6}$"
                    style={{ flex: 1 }}
                  />
                </div>
                <small style={{ color: '#666', fontSize: '0.85rem' }}>
                  Used for headers and navigation
                </small>
              </div>

              <div className="form-group">
                <label htmlFor="brandingLogoUrl" className="form-label">Logo URL (Optional)</label>
                <input
                  type="url"
                  id="brandingLogoUrl"
                  className="form-input"
                  value={brandingLogoUrl}
                  onChange={(e) => setBrandingLogoUrl(e.target.value)}
                  placeholder="https://example.com/logo.png"
                />
                <small style={{ color: '#666', fontSize: '0.85rem' }}>
                  Optional: URL to your company logo (leave blank to use text)
                </small>
              </div>

              <div className="form-group">
                <label htmlFor="brandingAdminEmail" className="form-label">Admin Email (Optional)</label>
                <input
                  type="email"
                  id="brandingAdminEmail"
                  className="form-input"
                  value={brandingAdminEmail}
                  onChange={(e) => setBrandingAdminEmail(e.target.value)}
                  placeholder="admin@yourcompany.com"
                />
                <small style={{ color: '#666', fontSize: '0.85rem' }}>
                  Email address to receive notifications when someone requests access. Requires SMTP configuration.
                </small>
              </div>

              <div style={{ 
                backgroundColor: '#f8f9fa', 
                padding: '1rem', 
                borderRadius: '4px',
                marginBottom: '1rem'
              }}>
                <strong style={{ display: 'block', marginBottom: '0.5rem' }}>Preview:</strong>
                <div style={{ 
                  display: 'flex', 
                  gap: '1rem', 
                  alignItems: 'center',
                  flexWrap: 'wrap'
                }}>
                  <div style={{ 
                    backgroundColor: brandingSecondaryColor, 
                    color: 'white',
                    padding: '0.5rem 1rem',
                    borderRadius: '4px',
                    fontSize: '1.1rem',
                    fontWeight: 'bold'
                  }}>
                    {brandingCompanyName || 'Company Name'}
                  </div>
                  <button 
                    type="button"
                    style={{ 
                      backgroundColor: brandingPrimaryColor, 
                      color: 'white',
                      padding: '0.5rem 1rem',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'default'
                    }}
                  >
                    Sample Button
                  </button>
                </div>
              </div>

              <button type="submit" className="btn-primary" disabled={loading}>
                {loading ? 'Updating...' : 'Update Branding'}
              </button>
            </form>
          </div>
        )}
      </main>

      {showPasswordModal && generatedPassword && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '2rem',
            borderRadius: '8px',
            maxWidth: '500px',
            width: '90%',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
          }}>
            <h2 style={{ marginBottom: '1rem', color: '#333' }}>User Account Created!</h2>
            <p style={{ marginBottom: '1rem', color: '#666' }}>
              A new user account has been created for <strong>{generatedPassword.companyName}</strong>.
            </p>
            <div style={{ 
              backgroundColor: '#f8f9fa', 
              padding: '1rem', 
              borderRadius: '4px', 
              marginBottom: '1rem',
              border: '1px solid #dee2e6'
            }}>
              <div style={{ marginBottom: '0.5rem' }}>
                <strong>Email:</strong> <span style={{ fontFamily: 'monospace' }}>{generatedPassword.email}</span>
              </div>
              <div style={{ marginBottom: '0.5rem' }}>
                <strong>Password:</strong> <span style={{ 
                  fontFamily: 'monospace', 
                  fontSize: '1.1rem',
                  color: '#d63384',
                  fontWeight: 'bold'
                }}>{generatedPassword.password}</span>
              </div>
            </div>
            <p style={{ marginBottom: '1.5rem', color: '#856404', backgroundColor: '#fff3cd', padding: '0.75rem', borderRadius: '4px', fontSize: '0.9rem' }}>
              <strong>Important:</strong> Copy this password now and send it to the client securely. You won't be able to see it again!
            </p>
            <div style={{ display: 'flex', gap: '1rem' }}>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(`Email: ${generatedPassword.email}\nPassword: ${generatedPassword.password}`);
                  alert('Credentials copied to clipboard!');
                }}
                className="btn-primary"
                style={{ flex: 1 }}
              >
                Copy Credentials
              </button>
              <button
                onClick={() => {
                  setShowPasswordModal(false);
                  setGeneratedPassword(null);
                }}
                className="btn-primary"
                style={{ flex: 1, backgroundColor: '#6c757d' }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminPanel;
